import * as yup from "yup";
import { MAX_BANNERS, ensureOneMain, normalizeBanners, type Banner } from "@/shared/upload/banners";

export { MAX_BANNERS };

/** Backend Joi (logics/branch/create.ts, update.ts): name and nameEng max 255; address.ts: parts max 100, description 500. */
export const NAME_MAX = 255;
export const ADDRESS_PART_MAX = 100;
export const ADDRESS_DESCRIPTION_MAX = 500;

export type BranchInfoValues = {
  name: string;
  nameEng: string;
  type: string;
  isBuyer: boolean;
  isSupplier: boolean;
  /** Business ids. */
  buyers: string[];
  suppliers: string[];
};

export type BranchAddressValues = {
  province: string;
  district: string;
  khoroo: string;
  khoroolol: string;
  khotkhonBair: string;
  khashaaDavkhar: string;
  khaalgaDugaar: string;
  branchAddress: string;
  locationLat: string;
  locationLng: string;
};

export type BranchMarketing = { banners: Banner[]; logo: string | null };

export const DEFAULT_INFO: BranchInfoValues = {
  name: "",
  nameEng: "",
  type: "",
  isBuyer: false,
  isSupplier: false,
  buyers: [],
  suppliers: [],
};

export const DEFAULT_ADDRESS: BranchAddressValues = {
  province: "",
  district: "",
  khoroo: "",
  khoroolol: "",
  khotkhonBair: "",
  khashaaDavkhar: "",
  khaalgaDugaar: "",
  branchAddress: "",
  locationLat: "",
  locationLng: "",
};

export const DEFAULT_MARKETING: BranchMarketing = { banners: [], logo: null };

type BranchData = Record<string, any>;

/** GET /prt/branch/:id answers buyers/suppliers as BusinessBranch link rows ({ id: link id, businessId }); the list answers businesses ({ id }). */
const linkedBusinessIds = (rows: unknown): string[] =>
  Array.isArray(rows)
    ? rows.map((row: any) => row?.businessId ?? row?.business?.id ?? row?.id).filter((id: unknown): id is string => Boolean(id))
    : [];

export function toInfoValues(data: BranchData | null | undefined): BranchInfoValues {
  return {
    name: data?.name ?? "",
    nameEng: data?.nameEng ?? "",
    type: data?.type ?? "",
    isBuyer: data?.isBuyer === true,
    isSupplier: data?.isSupplier === true,
    buyers: linkedBusinessIds(data?.buyers),
    suppliers: linkedBusinessIds(data?.suppliers),
  };
}

const coordinateText = (value: unknown) => (value === null || value === undefined || value === "" ? "" : String(value));

export function toAddressValues(data: BranchData | null | undefined): BranchAddressValues {
  return {
    province: data?.province ?? "",
    district: data?.district ?? "",
    khoroo: data?.khoroo ?? "",
    khoroolol: data?.khoroolol ?? "",
    khotkhonBair: data?.khotkhonBair ?? "",
    khashaaDavkhar: data?.khashaaDavkhar ?? "",
    khaalgaDugaar: data?.khaalgaDugaar ?? "",
    branchAddress: data?.branchAddress ?? "",
    locationLat: coordinateText(data?.locationLat),
    locationLng: coordinateText(data?.locationLng),
  };
}

export function toMarketing(data: BranchData | null | undefined): BranchMarketing {
  return {
    banners: normalizeBanners(
      Array.isArray(data?.profileBanners) ? data.profileBanners.map((b: any) => ({ url: b?.url, isMain: Boolean(b?.isMain) })) : [],
    ),
    logo: data?.logo ?? null,
  };
}

export function isMarketingEqual(a: BranchMarketing, b: BranchMarketing): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** True once the branch has a stored address (province is the first required part). */
export const hasStoredAddress = (data: BranchData | null | undefined): boolean => Boolean(data?.province && data?.locationLat !== null && data?.locationLat !== undefined);

type Translate = (key: string, opts?: Record<string, unknown>) => string;

export function buildInfoSchema(tCommon: Translate, t: Translate) {
  return yup.object({
    name: yup
      .string()
      .trim()
      .required(tCommon("required"))
      .matches(/^[А-Яа-яҮүӨөЁё0-9\s.-]{2,255}$/, { message: tCommon("validation.cyrillicOnlyShort"), excludeEmptyString: true }),
    nameEng: yup
      .string()
      .trim()
      .required(tCommon("required"))
      .matches(/^[A-Za-z0-9\s.-]{2,255}$/, { message: tCommon("validation.latinOnlyShort"), excludeEmptyString: true }),
    type: yup.string().required(tCommon("required")),
    isBuyer: yup.boolean().required(),
    isSupplier: yup.boolean().required(),
    buyers: yup.array().of(yup.string().required()).when("isBuyer", ([isBuyer], field) => (isBuyer ? field.min(1, t("form.pickBusiness")) : field)),
    suppliers: yup
      .array()
      .of(yup.string().required())
      .when("isSupplier", ([isSupplier], field) => (isSupplier ? field.min(1, t("form.pickBusiness")) : field)),
  });
}

const coordinate = (tCommon: Translate, limit: number) =>
  yup
    .number()
    .transform((value, original) => (original === "" || original === null ? undefined : value))
    .typeError(tCommon("validation.numberOnly"))
    .min(-limit, tCommon("validation.numberOnly"))
    .max(limit, tCommon("validation.numberOnly"))
    .required(tCommon("required"));

export function buildAddressSchema(tCommon: Translate) {
  const required = yup.string().trim().required(tCommon("required"));
  return yup.object({
    province: yup.string().typeError(tCommon("required")).trim().required(tCommon("required")),
    district: yup.string().typeError(tCommon("required")).trim().required(tCommon("required")),
    khoroo: yup.string().typeError(tCommon("required")).trim().required(tCommon("required")),
    khoroolol: required.max(ADDRESS_PART_MAX),
    khotkhonBair: required.max(ADDRESS_PART_MAX),
    khashaaDavkhar: required.max(ADDRESS_PART_MAX),
    khaalgaDugaar: required.max(ADDRESS_PART_MAX),
    branchAddress: required.max(ADDRESS_DESCRIPTION_MAX, tCommon("validation.maxChars", { count: ADDRESS_DESCRIPTION_MAX })),
    locationLat: coordinate(tCommon, 90),
    locationLng: coordinate(tCommon, 180),
  });
}

/** POST / PUT /prt/branch(/:id) body. isBuyer / isSupplier are booleans (not "true"/"false"); a switched-off side sends no businesses. */
export function buildInfoPayload(values: BranchInfoValues, marketing: BranchMarketing, isConfirmed: boolean) {
  return {
    name: values.name.trim(),
    nameEng: values.nameEng.trim(),
    type: values.type,
    logo: marketing.logo ?? null,
    profileBanners: ensureOneMain(marketing.banners),
    isBuyer: values.isBuyer,
    buyers: values.isBuyer ? values.buyers : [],
    isSupplier: values.isSupplier,
    suppliers: values.isSupplier ? values.suppliers : [],
    isConfirmed,
  };
}

/** PUT /prt/branch/:id/address body: coordinates are numbers (Joi.number().required()). */
export function buildAddressPayload(values: BranchAddressValues) {
  return {
    province: values.province,
    district: values.district,
    khoroo: values.khoroo,
    khoroolol: values.khoroolol.trim(),
    khotkhonBair: values.khotkhonBair.trim(),
    khashaaDavkhar: values.khashaaDavkhar.trim(),
    khaalgaDugaar: values.khaalgaDugaar.trim(),
    branchAddress: values.branchAddress.trim(),
    locationLat: Number(values.locationLat),
    locationLng: Number(values.locationLng),
  };
}
